/* eslint-disable import/namespace */
import { Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';

import { CustomerEditCsvContext } from './CustomerImportCSV';

import { CloseButton } from '@components/ActionButtons';
import {
  FormSectionContainer,
  FormButtonsContainer,
} from '@components/Container';
import {
  ActionableFormikInput,
  CancelButton,
  FormikCheckbox,
  FormikInput,
  SubmitButton,
} from '@components/Form';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import {
  billingAddressDefaultValue,
  billingAddressValidation,
  BillingAddressViewModel,
} from '@models/BillingAddress';
import { CustomerViewModel } from '@models/Customer';
import { CustomerService } from '@services/Customer';

export const CustomerImportCSVEditBillingAddress = () => {
  const { customer, setCustomer, updateCustomerInCsv } = useContext(
    CustomerEditCsvContext
  );
  const [showEditBillingAddress, setShowEditBillingAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadCustomerBillingAddress = () => {
    CustomerService.getCustomerBillingAddresses(Number(customer?.id)).then(
      (result) => {
        if (result.isSuccess && 'data' in result) {
          setCustomer((prevState) =>
            prevState
              ? {
                  ...prevState,
                  billingAddresses: result.data,
                }
              : prevState
          );
        }
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    loadCustomerBillingAddress();
  }, [customer?.id]);

  const formatAddress = (addr?: BillingAddressViewModel) => {
    if (!addr) return '';
    return `${addr.address1 || ''} ${addr.address2 || ''}, ${
      addr.city || ''
    }, ${addr.state || ''}, ${addr.country || ''} ${addr.postalCode || ''}`;
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <ActionableFormikInput
          label="Billling Address"
          name="fullBillingAddress"
          value={formatAddress()}
          onEditAction={() => {
            setShowEditBillingAddress(true);
          }}
          readonly={true}
        />

        {showEditBillingAddress && (
          <Modal show={true}>
            <div className="space-y-4">
              <div className="flex justify-end">
                <CloseButton onClick={() => setShowEditBillingAddress(false)} />
              </div>

              <Formik
                initialValues={{
                  ...(customer?.defaultBillingAddress ??
                    billingAddressDefaultValue),
                  fullBillingAddress: formatAddress(
                    customer?.defaultBillingAddress
                  ),
                }}
                validationSchema={billingAddressValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  if (!customer) return;

                  const updatedBillingAddress = {
                    ...customer.defaultBillingAddress,
                    ...values,
                    id: customer.defaultBillingAddress?.id ?? 0,
                  };

                  const updatedCustomer: CustomerViewModel = {
                    ...customer,
                    defaultBillingAddress: updatedBillingAddress,
                    billingAddresses: customer.billingAddresses?.map((addr) =>
                      addr.id === updatedBillingAddress.id
                        ? updatedBillingAddress
                        : addr
                    ) ?? [updatedBillingAddress],
                  };

                  setCustomer(updatedCustomer);
                  updateCustomerInCsv(updatedCustomer);

                  setShowEditBillingAddress(false);
                  actions.setSubmitting(false);
                }}
              >
                {(formikProps) => {
                  useEffect(() => {
                    formikProps.setFieldValue(
                      'fullBillingAddress',
                      formatAddress(formikProps.values)
                    );
                  }, [formikProps.values]);

                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput label="Address 1" name="address1" />
                          <FormikInput label="Address 2" name="address2" />
                          <FormikInput label="City" name="city" />
                          <FormikInput label="State" name="state" />
                          <FormikInput label="Country" name="country" />
                          <FormikInput label="Postal" name="postalCode" />
                          <FormikCheckbox label="Is Active" name="isActive" />

                          <Space />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => setShowEditBillingAddress(false)}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </div>
          </Modal>
        )}
      </>
    );
  }
};
